import { useSectionEmptyTracking } from '@/hooks/use-section-empty-tracking';
import { useTracking } from '@/libs/analytics';
import { logger } from '@/libs/logger';
import { CENTERED_HERO_PACK_IDS, PackHeroVariant } from '@/utils/constants';
import { MediaPackType } from '@/utils/media/types';
import { routes } from '@/utils/routes';
import { EnumFeature, EnumScreenName } from '@calm/evexia-analytics';
import { MediaNestedPack } from '@calm/evexia-web-sdk';
import { Theme } from '@emotion/react';
import { useActiveCircleId } from '@rhg/hooks/use-active-circle';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { usePacks } from '@rhg/hooks/use-packs';
import { SxProps } from '@rhg/libs/material';
import { WillBeNullProvider, useWillBeNull } from '@rhg/providers/will-be-null';
import { getViewAllHrefFromPackClass } from '@rhg/utils/pack-card';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import popularWithListenersMessages from '../home/popular-with-listeners/messages';
import { FullWidthLinearProgress } from '../ui/progress';
import { PackDetailsLayout } from './layout';
import { PackCarouselSection } from './pack-carousel-section';
import { PackPageGridSection } from './pack-page-grid-section';

export const styleCardFooter = {
    minHeight: 'auto',
    height: '100%',
};

interface PackPageProps {
    packClass?: string;
    showPopularWithSubtitle?: boolean;
    viewAll?: string;
    layoutSx?: SxProps<Theme>;
    /** Set only when navigating from a Discover row that wants an alternate
     *  hero treatment (e.g. Goals, Featured Collections). Absent on a direct
     *  deep link, which falls back to the default hero. */
    heroVariant?: PackHeroVariant;
    /** Row index of the row this page was reached from (Discover, a feed's
     *  nested rows, a pack-of-packs row, etc.), carried via query param.
     *  Absent on a direct deep link. */
    rowIndex?: number;
    /** Display type of the row this page was reached from. Carried
     *  alongside `rowIndex` via query param; absent on a direct deep link. */
    packDisplayType?: string;
}

const stylePackCarouselList = {
    'li > div.MuiBox-root': {
        height: '100%',
    },
};

export const MAX_CENTERED_ITEMS = 5;

/**
 * One pack-of-packs row. Reports its own emptiness up through the surrounding
 * `WillBeNullProvider` so the parent can recompute divider placement onto the
 * last section that's actually visible.
 */
function PackOfPacksItemSection({
    packItem,
    index,
    isRowCard,
    isLast,
    isMobile,
}: {
    packItem: MediaNestedPack;
    index: number;
    isRowCard: boolean;
    isLast: boolean;
    isMobile: boolean;
}) {
    const { setWillBeNull } = useWillBeNull();
    const handleEmpty = useCallback(() => setWillBeNull(true), [setWillBeNull]);
    const viewAllHref = getViewAllHrefFromPackClass({
        displayType: packItem.displayType,
        packClass: packItem.packClass,
        rowIndex: index,
    });

    return (
        <PackCarouselSection
            packClass={packItem.packClass}
            title={packItem.title}
            cardVariant="carouselSquare"
            viewAllHref={viewAllHref}
            noTitlePadding
            priorityItemCount={index === 0 ? (isMobile ? 1 : 3) : 0}
            showDivider={isRowCard ? !isLast : true}
            onEmpty={handleEmpty}
            packCarouselProps={{
                listSx: stylePackCarouselList,
                globalFullBleed: true,
            }}
        />
    );
}

function PackPageWithPrograms({
    packClass,
    pack,
    showPopularWithSubtitle,
    heroVariant,
    rowIndex,
    packDisplayType,
}: {
    packClass: string;
    pack: MediaPackType;
    showPopularWithSubtitle?: boolean;
    heroVariant?: PackHeroVariant;
    rowIndex?: number;
    packDisplayType?: string;
}) {
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile();
    const { activeCircleId } = useActiveCircleId();

    const { trackAsPageNav, state } = useTracking();
    // Captured once at mount, before trackAsPageNav overwrites currentScreenName
    // with PackDetails — this is the screen the user arrived from (Discover,
    // DiscoverCategory, etc.), used as SourceScreenName.
    //
    // DiscoverCategory is only ever this route's own placeholder screen name
    // (the analytics-provider route-level effect always skip-lists this
    // route, so currentScreenName is never legitimately *set* to
    // DiscoverCategory — it can only be the initial boot value). So on a
    // direct deep link, where there was no real inbound screen, normalize it
    // to NotMapped rather than reporting a screen the user never visited.
    const initialScreenName = state.currentScreenName;
    const sourceScreenRef = useRef(
        initialScreenName === EnumScreenName.DiscoverCategory
            ? EnumScreenName.NotMapped
            : initialScreenName
    );
    const hasTrackedPageNav = useRef(false);

    useEffect(() => {
        // Fire the PackDetails screen view once per pack mount. The route is
        // opted out of the provider-level fire, so the page owns it here.
        if (hasTrackedPageNav.current) return;
        hasTrackedPageNav.current = true;
        trackAsPageNav(
            EnumScreenName.PackDetails,
            EnumFeature.Discover,
            undefined,
            {
                PackClass: packClass,
                SourceScreenName: sourceScreenRef.current,
                ...(rowIndex !== undefined ? { RowIndex: rowIndex } : {}),
                ...(packDisplayType
                    ? { PackDisplayType: packDisplayType }
                    : {}),
            }
        );
        // rowIndex/packDisplayType are in the deps purely to satisfy
        // exhaustive-deps — hasTrackedPageNav.current is already true by the
        // time either could change mid-mount, so a change never re-fires this.
    }, [trackAsPageNav, packClass, rowIndex, packDisplayType]);

    // The grid client-paginates its cards and each card fetches its own
    // program/pack metadata as it's revealed, so we no longer block first paint
    // on prefetching every item's metadata up front. The carousel
    // (pack-of-packs) branch likewise lets each PackCarouselSection fetch on
    // demand.

    const popularWithSubtitle = formatMessage(
        popularWithListenersMessages.popularSectionHeadline
    );

    const isPackOfPacks =
        pack?.items?.every(({ type }) => type === 'pack') ?? false;
    const title = pack?.title ?? '';
    const subtitle = showPopularWithSubtitle
        ? popularWithSubtitle
        : pack?.subtitle ?? '';
    const packItemsLength = pack?.items.length ?? 0;
    const { markEmpty, lastVisibleIndex } = useSectionEmptyTracking(
        `${activeCircleId}:${packClass}`,
        packItemsLength
    );
    // Falls back to the allowlist when no row supplied heroVariant — content
    // can double-dip across rows/surfaces the query param can't track (see
    // CENTERED_HERO_PACK_IDS).
    const effectiveHeroVariant =
        heroVariant ??
        (CENTERED_HERO_PACK_IDS.has(packClass)
            ? PackHeroVariant.Centered
            : undefined);

    if (isPackOfPacks) {
        const isRowCard = pack?.displayType === 'row-card';

        return (
            <PackDetailsLayout
                title={title}
                subtitle={subtitle}
                packClass={packClass}
                imageData={isRowCard ? pack?.image : undefined}
                heroVariant={effectiveHeroVariant}
            >
                {pack?.items.map((packItem, i) => {
                    if (packItem?.type !== 'pack') {
                        return null;
                    }

                    return (
                        <WillBeNullProvider
                            key={packItem.packClass}
                            handleWillBeNull={() => markEmpty(i)}
                        >
                            <div data-testid="PackPageSection">
                                <PackOfPacksItemSection
                                    packItem={packItem}
                                    index={i}
                                    isRowCard={isRowCard}
                                    isLast={i === lastVisibleIndex}
                                    isMobile={isMobile}
                                />
                            </div>
                        </WillBeNullProvider>
                    );
                })}
            </PackDetailsLayout>
        );
    }

    return (
        <PackDetailsLayout
            title={title}
            reversed={showPopularWithSubtitle}
            subtitle={subtitle}
            packClass={packClass}
            heroVariant={effectiveHeroVariant}
        >
            <PackPageGridSection pack={pack} />
        </PackDetailsLayout>
    );
}

export const PackPage = ({
    packClass,
    showPopularWithSubtitle,
    heroVariant,
    rowIndex,
    packDisplayType,
}: PackPageProps) => {
    const { activeCircleId } = useActiveCircleId();
    const router = useRouter();
    const { isFetched, packs } = usePacks(
        packClass ? [packClass] : [],
        activeCircleId
    );
    const pack = packs?.[0];

    useEffect(() => {
        // redirect if no pack
        if (isFetched && (!pack || !packClass)) {
            router
                .push(routes.discover)
                .catch((err) =>
                    logger.error(
                        'Failed to redirect to discover',
                        {},
                        err as Error
                    )
                );
        }
    }, [isFetched, pack, packClass, router]);

    if (!isFetched) {
        return <FullWidthLinearProgress />;
    }

    if (!pack || !packClass) {
        // This will be redirected
        return <FullWidthLinearProgress />;
    }

    // key={packClass} so navigating pack → pack (e.g. a nested pack-of-packs
    // "View All") remounts: the PackDetails screen view re-fires for the new
    // pack instead of carrying over the previous one's tracked-state.
    return (
        <PackPageWithPrograms
            key={packClass}
            packClass={packClass}
            pack={pack}
            showPopularWithSubtitle={showPopularWithSubtitle}
            heroVariant={heroVariant}
            rowIndex={rowIndex}
            packDisplayType={packDisplayType}
        />
    );
};
